"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APIShareRegionalTrendResponseSchema = exports.APIShareRegionalTrendItemSchema = exports.APIShareRegionalTrendRequestSchema = exports.APISharedProfileResponseSchema = exports.APISharedLifestyleSchema = exports.APISharedLifestyleDaySchema = exports.APISharedProfileHeaderSchema = exports.APISharedRoutineItemSchema = exports.APISharedMetricDefinitionSchema = exports.APISharedMetricSeriesSchema = exports.APISharedMetricValueSchema = exports.APISharedRegionalDataSchema = exports.APISharedProfileRequestSchema = exports.APIShareVerifyResponseSchema = exports.APIShareVerifyRequestSchema = exports.ShareVerifyError = exports.shareVerifyErrorReason = exports.APISharePreviewRegionalTrendRequestSchema = exports.APISharePreviewRequestSchema = exports.APIShareUpdateRequestSchema = exports.APIShareMineSchema = exports.APIShareRegenerateResponseSchema = exports.APIShareRegenerateRequestSchema = exports.shareLinkStatusEnum = exports.SHARE_EXPIRY_MS = exports.DEFAULT_SHARE_EXPIRY = exports.shareExpiryEnum = void 0;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const metric_dashboard_1 = require("./metric_dashboard");
const routine_markers_1 = require("./routine_markers");
/**
 * Shareable Skin Profile — shared contract types.
 *
 * A live, link-only, revocable web view of a user's skin profile that they can
 * share with someone. See docs/adr/0018-public-share-surface.md and the
 * implementation spec. NO `insight` / interpretation fields ever appear here —
 * the shared profile presents measurements only. Photos, Metrics, and Routine
 * are independent sections (any combination is shareable).
 */
/**
 * User-chosen link expiry, picked once at generate time. Fixed thereafter — app
 * activity never extends it; regenerating is the only way to get a fresh window.
 * `never` means the link has no expiry (persisted as a null `expires_at`).
 */
exports.shareExpiryEnum = [
    '24h',
    '7d',
    '30d',
    '60d',
    '90d',
    'never',
];
exports.DEFAULT_SHARE_EXPIRY = '7d';
const DAY_MS = 24 * 60 * 60_000;
/** Milliseconds per finite expiry. `never` is absent — it resolves to a null expiry. */
exports.SHARE_EXPIRY_MS = {
    '24h': DAY_MS,
    '7d': 7 * DAY_MS,
    '30d': 30 * DAY_MS,
    '60d': 60 * DAY_MS,
    '90d': 90 * DAY_MS,
};
/**
 * Lifecycle of a share link. One `active` link per user (DB partial unique
 * index); `regenerate` supersedes the prior one; `revoke` is the kill switch.
 */
exports.shareLinkStatusEnum = ['active', 'revoked', 'superseded'];
/**
 * Authed request to mint a fresh link (link-only — no access code). `regenerate`
 * is the single mutation for create / re-pick-expiry / choose-what-to-include.
 * Photos, Metrics, and Routine are INDEPENDENT sections; at least one must be on
 * (no empty share). Photos ride inside the metric values, but the two are
 * decoupled server-side (see toSharedProfile) so any combination is valid.
 */
exports.APIShareRegenerateRequestSchema = zod_1.z
    .object({
    includePhotos: zod_1.z.boolean(),
    includeMetrics: zod_1.z.boolean(),
    includeRoutine: zod_1.z.boolean(),
    // Lifestyle (wearables: sleep / HRV / steps / activity). Default OFF in the
    // UI — it is the user's own health data (see spec §14).
    includeLifestyle: zod_1.z.boolean(),
    expiry: zod_1.z.enum(exports.shareExpiryEnum).default(exports.DEFAULT_SHARE_EXPIRY),
})
    .refine((d) => d.includePhotos ||
    d.includeMetrics ||
    d.includeRoutine ||
    d.includeLifestyle, { message: 'at least one section must be shared' });
/**
 * Response to `regenerate`. Link-only: no access code is minted or returned.
 * `expiresAt` is null when the chosen expiry is `never`.
 */
exports.APIShareRegenerateResponseSchema = zod_1.z.object({
    url: zod_1.z.string(),
    expiresAt: zod_1.z.string().nullable(), // ISO 8601, null = never
});
/**
 * The user's one active link, as surfaced in-app (`getMine`). `getMine` returns
 * `null` when there is no active link. `expiresAt` is null for a never-expiring
 * link. Carries the current include flags so the owner control bar can reflect
 * them.
 */
exports.APIShareMineSchema = zod_1.z.object({
    url: zod_1.z.string(),
    includePhotos: zod_1.z.boolean(),
    includeMetrics: zod_1.z.boolean(),
    includeRoutine: zod_1.z.boolean(),
    includeLifestyle: zod_1.z.boolean(),
    expiresAt: zod_1.z.string().nullable(), // ISO 8601, null = never
    lastViewedAt: zod_1.z.string().nullable(), // ISO 8601, null until first opened
    viewCount: zod_1.z.number(),
});
/**
 * Authed request to change what an active link includes, in place (no new
 * token). Any subset of the three flags; at least one field must be present.
 * The all-off case is rejected in the controller (a live link must show
 * something). Expiry is NOT editable in place — it is fixed at generate time.
 */
exports.APIShareUpdateRequestSchema = zod_1.z
    .object({
    includePhotos: zod_1.z.boolean().optional(),
    includeMetrics: zod_1.z.boolean().optional(),
    includeRoutine: zod_1.z.boolean().optional(),
    includeLifestyle: zod_1.z.boolean().optional(),
})
    .refine((d) => Object.values(d).some((v) => v !== undefined), {
    message: 'at least one field required',
});
/**
 * Authed request for the owner's own live preview of their artifact, before any
 * link exists. Returns the full {@link APISharedProfileResponse} (all sections);
 * the owner control bar hides sections client-side. Never bumps a view count.
 */
exports.APISharePreviewRequestSchema = zod_1.z.object({
    locale: zod_1.z.enum(enums_1.supportedLocaleEnum).optional(),
});
/** Authed request for the owner preview's per-region trend for one metric. */
exports.APISharePreviewRegionalTrendRequestSchema = zod_1.z.object({
    techName: zod_1.z.enum(enums_1.techNameEnum),
    locale: zod_1.z.enum(enums_1.supportedLocaleEnum).optional(),
});
/**
 * Machine-readable reasons a public verify can fail. The backend emits the
 * reason (never user copy — see no-backend-user-strings); the web app maps each
 * to localized copy (e.g. `expired` → "This link has expired. Ask {firstName}
 * to generate a new one."). `not_found` intentionally conflates unknown /
 * revoked / superseded so a scraper can't distinguish them.
 */
exports.shareVerifyErrorReason = [
    'not_found',
    'expired',
    'invalid_code',
    'locked',
    // getProfile: the short-lived view session has lapsed — the web app should
    // re-prompt for the access code (re-verify), the link itself may still be live.
    'session_expired',
];
/**
 * Transport-neutral error the share controller throws on a denied verify. The
 * tRPC layer (router) maps `reason` to a TRPCError code — keeps @trpc/server out
 * of the orchestration layer (ADR-0007). `message` IS the machine reason.
 */
class ShareVerifyError extends Error {
    reason;
    constructor(reason) {
        super(reason);
        this.reason = reason;
        this.name = 'ShareVerifyError';
    }
}
exports.ShareVerifyError = ShareVerifyError;
/**
 * Public request to open a link. Link-only shares carry no code, so `accessCode`
 * is optional: when the link has a null `access_code_hash` the code step is
 * skipped (throttling still applies). Legacy coded links still require it. The
 * exchange yields a short-lived view session either way.
 */
exports.APIShareVerifyRequestSchema = zod_1.z.object({
    // Exact share-token shape (128-bit, 22-char base62) — reject malformed/oversized
    // identifiers at the edge before they reach the DB or the rate limiter.
    token: zod_1.z.string().regex(/^[0-9A-Za-z]{22}$/),
    accessCode: zod_1.z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
});
/**
 * Response to `verify`: an opaque, short-lived view token the web app presents
 * to `getProfile` / `getRegionalTrend` instead of re-sending the access code.
 */
exports.APIShareVerifyResponseSchema = zod_1.z.object({
    viewToken: zod_1.z.string(),
    expiresInSeconds: zod_1.z.number(),
});
/* ── getProfile (public read) ─────────────────────────────────────────────── */
/**
 * Public request: exchange a verified view token for the full shared profile.
 * The web app presents the opaque `viewToken` minted by `verify` (never the
 * access code again).
 */
exports.APISharedProfileRequestSchema = zod_1.z.object({
    viewToken: zod_1.z.string().regex(/^[0-9A-Za-z]{22}$/),
    locale: zod_1.z.enum(enums_1.supportedLocaleEnum).optional(),
});
/**
 * Regional entry — an EXPLICIT allowlist of measurement fields only. No
 * `insight`; and defined field-by-field (not `.omit()`) so a future
 * interpretive field on the deep-dive schema can't silently flow through here.
 */
exports.APISharedRegionalDataSchema = zod_1.z.object({
    area: zod_1.z.enum(enums_1.areaEnum),
    ema: zod_1.z.number().nullish(),
    emaFast: zod_1.z.number().nullish(),
    baselineEma: zod_1.z.number().nullish(),
    landmark: metric_dashboard_1.FaceLandmarkSchema.nullish(),
    landmarks: metric_dashboard_1.FaceLandmarkSchema.array().optional(),
});
/**
 * Per-scan metric value — an EXPLICIT allowlist of MEASUREMENTS ONLY. Excludes
 * every interpretive field (`insight`, `rating`, `scanRecommendation`) — the
 * provider draws their own conclusions (ADR-0018, "measurements without
 * interpretation"). `imageUrl` is gated (empty unless the user opted into
 * photo sharing). Field-by-field so nothing interpretive can auto-enter.
 */
exports.APISharedMetricValueSchema = zod_1.z.object({
    scanId: zod_1.z.string(),
    techName: zod_1.z.enum(enums_1.techNameEnum),
    area: zod_1.z.enum(enums_1.areaEnum),
    scanAt: zod_1.z.string(), // ISO 8601
    ema: zod_1.z.number().nullish(), // Trend
    baselineEma: zod_1.z.number().nullish(), // Baseline
    emaFast: zod_1.z.number().nullish(), // Fast EMA
    imageUrl: zod_1.z.string(), // signed CDN url, or '' when photos not shared
    imageQuality: metric_dashboard_1.APIMetricDashboardDeepDiveImageQualitySchema,
    landmarkImageWidth: zod_1.z.number().nullish(),
    landmarkImageHeight: zod_1.z.number().nullish(),
    regionalData: zod_1.z.array(exports.APISharedRegionalDataSchema).nullish(),
});
/** A metric's full (face-level) time series for the shared profile. */
exports.APISharedMetricSeriesSchema = zod_1.z.object({
    techName: zod_1.z.enum(enums_1.techNameEnum),
    data: zod_1.z.array(exports.APISharedMetricValueSchema),
});
/** Plain metric definition (kept as-is — it is factual, not interpretive). */
exports.APISharedMetricDefinitionSchema = metric_dashboard_1.APIMetricDashboardDeepDiveDefinitionSchema.extend({
    techName: zod_1.z.enum(enums_1.techNameEnum),
});
/** An active routine item, with its ingredients and change history. */
exports.APISharedRoutineItemSchema = zod_1.z.object({
    productName: zod_1.z.string(),
    category: zod_1.z.enum(enums_1.routineSlotEnum).nullable(),
    schedule: zod_1.z.array(zod_1.z.object({
        day: zod_1.z.enum(enums_1.dayOfWeekEnum),
        period: zod_1.z.enum(enums_1.periodEnum),
    })),
    asNeeded: zod_1.z.boolean(),
    since: zod_1.z.string(), // ISO 8601 (occurredAt)
    ingredients: zod_1.z.array(zod_1.z.string()), // split INCI from pdb_product.ingredients
    events: zod_1.z.array(routine_markers_1.RoutineChangeMarkerSchema), // this product's change history
});
/**
 * First-name-only header + tracking summary (no full name — see ADR-0018).
 * `includesPhotos` / `includesMetrics` are the two render flags the recipient
 * uses to show/hide sections on the shared scrubber layout (routine visibility
 * follows from the routine array being empty). Photos and Metrics are
 * independent: any of photos-only / metrics-only / both is valid.
 */
exports.APISharedProfileHeaderSchema = zod_1.z.object({
    firstName: zod_1.z.string(),
    scanCount: zod_1.z.number(),
    trackingWeeks: zod_1.z.number(),
    firstScanAt: zod_1.z.string().nullable(), // ISO 8601
    lastScanAt: zod_1.z.string().nullable(), // ISO 8601
    includesPhotos: zod_1.z.boolean(),
    includesMetrics: zod_1.z.boolean(),
    includesLifestyle: zod_1.z.boolean(),
});
/**
 * One day of shared LIFESTYLE (wearable) measurements — an EXPLICIT allowlist of
 * MEASUREMENTS ONLY (per ADR-0018; explicit `z.object`, never `.omit()`, so no
 * derived/interpretive wearable field can silently flow through). `hrvAvgMs` is
 * `wearable_daily_summary.hrv_avg` (RMSSD ?? SDNN) in milliseconds — the
 * "Stress Recovery" signal (better provider coverage than a raw stress score).
 * All fields nullish: a provider may supply only some (e.g. HRV null on some
 * devices), and the section degrades gracefully. See spec §14.
 */
exports.APISharedLifestyleDaySchema = zod_1.z.object({
    date: zod_1.z.string(), // ISO date, YYYY-MM-DD
    sleepDurationMinutes: zod_1.z.number().nullish(),
    hrvAvgMs: zod_1.z.number().nullish(),
    steps: zod_1.z.number().nullish(),
    exerciseMinutes: zod_1.z.number().nullish(), // activity
});
/** The shared lifestyle time series (empty when not shared / no wearable data). */
exports.APISharedLifestyleSchema = zod_1.z.object({
    data: zod_1.z.array(exports.APISharedLifestyleDaySchema),
});
/**
 * The full payload the shared web view renders. NO `insight` / interpretation
 * anywhere — measurements + definitions + routine + change history + lifestyle
 * (wearable measurements) only.
 */
exports.APISharedProfileResponseSchema = zod_1.z.object({
    header: exports.APISharedProfileHeaderSchema,
    metrics: zod_1.z.array(exports.APISharedMetricSeriesSchema),
    definitions: zod_1.z.array(exports.APISharedMetricDefinitionSchema),
    routine: zod_1.z.array(exports.APISharedRoutineItemSchema),
    markers: zod_1.z.array(routine_markers_1.RoutineChangeMarkerSchema),
    lifestyle: exports.APISharedLifestyleSchema,
});
/* ── getRegionalTrend (public read) ───────────────────────────────────────── */
/**
 * Public request: the per-region trend for ONE metric across all scans. Fetched
 * LAZILY (separate from getProfile) so the base profile payload stays lean and
 * regional geometry is pulled only when the viewer opens region detail — P1
 * intentionally kept regional data off the historical scans in getProfile.
 */
exports.APIShareRegionalTrendRequestSchema = zod_1.z.object({
    viewToken: zod_1.z.string().regex(/^[0-9A-Za-z]{22}$/),
    techName: zod_1.z.enum(enums_1.techNameEnum),
    locale: zod_1.z.enum(enums_1.supportedLocaleEnum).optional(),
});
/**
 * Per-scan regional readings for the metric — MEASUREMENTS ONLY. Reuses the
 * insight-free {@link APISharedRegionalDataSchema} allowlist for each region.
 * Landmark geometry (coords + image dims) is biometric and rides the SAME photo
 * opt-in as the scan photo: absent when the user did not share photos.
 */
exports.APIShareRegionalTrendItemSchema = zod_1.z.object({
    scanId: zod_1.z.string(),
    scanAt: zod_1.z.string(), // ISO 8601
    landmarkImageWidth: zod_1.z.number().nullish(),
    landmarkImageHeight: zod_1.z.number().nullish(),
    regionalData: zod_1.z.array(exports.APISharedRegionalDataSchema),
});
/** A metric's per-region series across every scan, insight-free. */
exports.APIShareRegionalTrendResponseSchema = zod_1.z.object({
    techName: zod_1.z.enum(enums_1.techNameEnum),
    data: zod_1.z.array(exports.APIShareRegionalTrendItemSchema),
});
//# sourceMappingURL=share.js.map